import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Tag } from "lucide-react";
import api from "@/lib/api";
import { formatPriceCzk } from "@/lib/categories";

export default function SearchSuggest({ value, onChange, inputId = "search-input" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [terms, setTerms] = useState([]);
  const wrapRef = useRef(null);
  const timer = useRef(null);

  const term = (value || "").trim();
  const rows = [
    ...(term ? [{ kind: "search", key: `search:${term}`, data: { q: term } }] : []),
    ...terms
      .filter((t) => (t.q || "").trim().toLowerCase() !== term.toLowerCase())
      .map((t) => ({ kind: "term", key: `term:${t.q}`, data: t })),
    ...categories.map((c) => ({ kind: "cat", key: `${c.slug}/${c.sub || ""}`, data: c })),
    ...listings.map((l) => ({ kind: "ad", key: l.id, data: l })),
  ];

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (term.length < 1) {
      setListings([]);
      setCategories([]);
      setTerms([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/listings/suggest", { params: { q: term, limit: 8 } });
        setListings(data.listings || []);
        setCategories(data.categories || []);
        setTerms(data.terms || []);
        setOpen(true);
        setActive(0);
      } catch (_) {
        setListings([]);
        setCategories([]);
        setTerms([]);
        setOpen(true);
        setActive(0);
      }
    }, 120);
    return () => clearTimeout(timer.current);
  }, [term]);

  const go = (row) => {
    setOpen(false);
    if (row.kind === "ad") {
      navigate(`/inzerat/${row.data.id}`);
      return;
    }
    if (row.kind === "cat") {
      const path = row.data.sub
        ? `/kategorie/${row.data.slug}/${row.data.sub}`
        : `/kategorie/${row.data.slug}`;
      navigate(path);
      return;
    }
    const q = (row.data.q || term).trim();
    const input = document.getElementById(inputId);
    if (q !== term) onChange(q);
    if (input?.form) {
      if (q !== term) {
        onChange(q);
        setTimeout(() => input.form.requestSubmit(), 0);
        return;
      }
      input.form.requestSubmit();
    }
  };

  const onKey = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open || rows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && active >= 0 && rows[active] && rows[active].kind !== "search") {
      e.preventDefault();
      go(rows[active]);
    }
  };

  return (
    <div className="relative flex-1" ref={wrapRef}>
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none z-10" />
      <input
        id={inputId}
        type="text"
        placeholder="Co hledáš? Bagr, generátor, cihly, cívka…"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => { if (term) setOpen(true); }}
        onKeyDown={onKey}
        autoComplete="off"
        className="stb-input pl-11 h-full"
        data-testid="search-input"
      />
      {open && rows.length > 0 && (
        <ul
          className="absolute z-40 top-full left-0 right-0 mt-1 bg-[#0E0E0E] border-2 border-[#262626] max-h-96 overflow-y-auto shadow-2xl"
          data-testid="search-suggestions"
        >
          {rows.map((row, i) => (
            <li key={row.key}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  go(row);
                }}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 ${
                  i === active ? "bg-[#1a1a1a]" : "hover:bg-[#1a1a1a]"
                }`}
                data-testid={`search-suggestion-${i}`}
              >
                {row.kind === "search" && (
                  <>
                    <Search size={14} className="text-[#FF6B00] shrink-0" />
                    <span className="text-white text-sm">
                      Hledat <span className="text-[#FF6B00]">„{row.data.q}“</span>
                    </span>
                    <span className="ml-auto stb-mono text-[10px] text-[#A3A3A3]">vše</span>
                  </>
                )}
                {row.kind === "term" && (
                  <>
                    <Search size={14} className="text-[#A3A3A3] shrink-0" />
                    <span className="text-white text-sm">{row.data.q}</span>
                    <span className="ml-auto stb-mono text-[10px] text-[#A3A3A3]">návrh</span>
                  </>
                )}
                {row.kind === "cat" && (
                  <>
                    <Tag size={14} className="text-[#FF6B00] shrink-0" />
                    <span className="text-white text-sm">{row.data.name}</span>
                    <span className="ml-auto stb-mono text-[10px] text-[#A3A3A3]">kategorie</span>
                  </>
                )}
                {row.kind === "ad" && (
                  <>
                    {row.data.photos?.[0] ? (
                      <img src={row.data.photos[0]} alt="" className="w-10 h-10 object-cover border border-[#262626] shrink-0" />
                    ) : (
                      <Search size={14} className="text-[#FF6B00] shrink-0" />
                    )}
                    <span className="min-w-0">
                      <span className="block text-white text-sm truncate">{row.data.title}</span>
                      <span className="block stb-mono text-[10px] text-[#A3A3A3] truncate">
                        {row.data.location || "Česko"}
                        {row.data.price != null ? ` · ${formatPriceCzk(row.data.price)}` : ""}
                      </span>
                    </span>
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
